import React, { useState, useMemo } from 'react';
import cx from 'classnames';

import CheckboxGroup from 'components/form/CheckboxGroup';
import RadioGroup from 'components/form/RadioGroup';

import type { CustomFieldOptionItem, CustomFieldRendererProps } from './customField.types';

import styles from './CustomFieldRenderer.scss';

function getOptionDisplay(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.displayText;
}

function getOptionValue(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.value;
}

const OPTION_SCHEMA = { id: 'value' as const, text: 'displayText' as const };

const EMPTY_CUSTOM_FIELD_OPTIONS: CustomFieldOptionItem[] = [];

export function CustomFieldRenderer({ field, disabled = true }: CustomFieldRendererProps) {
  const optionList = field.options ?? EMPTY_CUSTOM_FIELD_OPTIONS;
  const [radioSelected, setRadioSelected] = useState<string | null>(null);
  const [checkboxSelected, setCheckboxSelected] = useState<string[]>([]);

  const items = useMemo(
    () =>
      optionList.map(opt => ({
        value: getOptionValue(opt),
        displayText: getOptionDisplay(opt),
      })),
    [optionList]
  );

  const renderInput = () => {
    switch (field.fieldType) {
      case 'textarea':
        return (
          <textarea
            className={styles.fieldTextarea}
            disabled={disabled}
            placeholder={field.placeholder ?? ''}
            data-test={`customField-textarea-${field.fieldKey}`}
          />
        );

      case 'select_dd':
        return (
          <select
            className={styles.fieldSelect}
            disabled={disabled}
            data-test={`customField-select-${field.fieldKey}`}
          >
            <option value="">{field.placeholder ?? ''}</option>
            {optionList.map((option) => (
              <option
                key={getOptionValue(option)}
                value={getOptionValue(option)}
              >
                {getOptionDisplay(option)}
              </option>
            ))}
          </select>
        );

      case 'select_radio':
        return (
          <fieldset
            className={cx(styles.options, styles.optionsFieldset)}
            data-test={`customField-radio-${field.fieldKey}`}
          >
            <RadioGroup
              name={field.fieldKey}
              disabled={disabled}
              selected={radioSelected}
              schema={OPTION_SCHEMA}
              items={items}
              onChange={disabled ? () => undefined : setRadioSelected}
              doTranslate={false}
            />
          </fieldset>
        );

      case 'multi_select':
        return (
          <fieldset
            className={cx(styles.options, styles.optionsFieldset)}
            data-test={`customField-checkbox-${field.fieldKey}`}
          >
            <CheckboxGroup
              name={field.fieldKey}
              disabled={disabled}
              selected={checkboxSelected}
              schema={OPTION_SCHEMA}
              items={items}
              onChange={
                disabled
                  ? () => undefined
                  : (values: string[]) => setCheckboxSelected(values)
              }
            />
          </fieldset>
        );

      default:
        return (
          <input
            type="text"
            className={styles.fieldInput}
            disabled={disabled}
            placeholder={field.placeholder ?? ''}
            data-test={`customField-text-${field.fieldKey}`}
          />
        );
    }
  };

  return (
    <div
      className={cx(styles.fieldWrapper, { [styles.interactive]: !disabled })}
      data-test={`customFieldRenderer-${field.fieldKey}`}
    >
      <label className={styles.fieldLabel}>
        {field.fieldName}
      </label>
      {renderInput()}
    </div>
  );
}
